import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  first,
  map,
  merge,
  Subject,
  takeUntil,
} from 'rxjs';
import { MatMenuTrigger } from '@angular/material/menu';

import { TriggerAction } from '@shared/actions';
import { StateManagerProjectService } from '@state-manager';
import { CornerstoneService } from '@viewer/cornerstone.service';
import {
  CornerstoneTool,
  EraserToolComponentKeyActionName,
  EraserToolComponentKeyActionNameList,
  HotKey,
  HotKeySettings,
} from '@viewer/data';
import { BaseToolComponent } from '../base-tool/base-tool.component';
import {
  PointerShape,
  IEditImageToolSettings,
  IBrushSettings,
  ToolActivationMethod,
} from '../data';
import { KeyInteractionService } from 'src/app/interaction';

@Component({
  selector: 'app-eraser-tool',
  templateUrl: 'eraser-tool.component.html',
  styleUrls: ['eraser-tool.component.scss'],
})
export class EraserToolComponent
  extends BaseToolComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() settings: IEditImageToolSettings = {
    actionMode: 'button',
    label: 'Eraser',
    displayIcon: true,
    disableTooltip: false,
    displayCorner: true,
    displayHotKey: true,
    hideMenuBackdrop: true,
    menuPosition: {
      x: 'before',
      y: 'above',
    },
  };
  @Input() useHotKeys = true;

  @Output() toolActivated = new EventEmitter<ToolActivationMethod>();

  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;
  @ViewChild('pointerSettingsMenu') pointerSettingsMenu!: any;

  public eraserAction!: TriggerAction;
  public eraserSettings!: IBrushSettings;
  public isEraserActive$ = new BehaviorSubject<boolean>(false);

  private _unsubscribe$ = new Subject<void>();

  constructor(
    public stateManagerProjectService: StateManagerProjectService,
    private _cornerstoneService: CornerstoneService,
    private _elementRef: ElementRef,
    private _cdr: ChangeDetectorRef,
    private _keyInteractionService: KeyInteractionService
  ) {
    super(stateManagerProjectService);
  }

  ngOnInit(): void {
    this.stateManagerProjectService.eraserSettings$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((settings) => (this.eraserSettings = settings));
    this.eraserAction = new TriggerAction(
      this.settings.displayIcon ? 'eraser' : '',
      this.settings.label,
      this._onActivateToolBtnClick.bind(this)
    );

    this.eraserAction.isEnable = () =>
      combineLatest([
        this.isDisabled$,
        this.stateManagerProjectService.isDisabledAllToolsForChangeMask$,
      ]).pipe(
        map(
          ([isDisabled, isDisabledAllToolsForChangeMask]) =>
            !isDisabled && !isDisabledAllToolsForChangeMask
        )
      );

    this.stateManagerProjectService.currentStateOfToolMenu$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((currentStateOfToolMenu) => {
        if (currentStateOfToolMenu !== 'ERASER' && this.menuTrigger) {
          this.menuTrigger.closeMenu();
        }
      });

    this.stateManagerProjectService.currentTool$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((currentTool) => {
        this.isEraserActive$.next(this._checkEraserActivated(currentTool));
      });

    if (this.useHotKeys) {
      this._initializeHotKeys();
    }

    this._cornerstoneService.closeOpenedMenu$.pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => this.menuTrigger.closeMenu());
  }

  ngAfterViewInit(): void {
    if (this.settings.actionMode === 'button') {
      super.initializeMenuBackdrop(this.menuTrigger, this._elementRef);
    }

    if (this.settings.openMenu) {
      this.menuTrigger.openMenu();
    }

    if (this.settings.closeMenuCondition$) {
      this.settings.closeMenuCondition$
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe((toClose) => {
          if (toClose) {
            this.menuTrigger.closeMenu();
          }
        });
    }

    if (this.useHotKeys) {
      merge(
        this.menuTrigger.menuOpened.pipe(map(() => true)),
        this.menuTrigger.menuClosed.pipe(map(() => false))
      )
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe((isMenuOpened) => {
          this._keyInteractionService.setActivateState<EraserToolComponentKeyActionName>(
            'ExitEraserMenu',
            isMenuOpened
          );
        });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._unsubscribe$.next();
    this._unsubscribe$.complete();

    if (this.useHotKeys) {
      this._keyInteractionService.removeKeySettingsList([
        ...EraserToolComponentKeyActionNameList,
      ]);
    }
  }

  public onSizeChanged(size: number): void {
    this.stateManagerProjectService.eraserSettings = {
      ...this.eraserSettings,
      size: size,
    };
  }

  public onShapeChanged(shape: PointerShape): void {
    this.stateManagerProjectService.eraserSettings = {
      ...this.eraserSettings,
      shape: shape,
    };
    this._activateTool();
  }

  public onEraserActionContextMenu(event: MouseEvent): void {
    this.eraserAction
      .isEnable()
      .pipe(first())
      .subscribe((isEnable) => {
        if (!isEnable) {
          return;
        }
        
        event.preventDefault();
        this.menuTrigger.toggleMenu();

        if (
          this._checkEraserActivated(
            this.stateManagerProjectService.currentTool
          )
        ) {
          return;
        }

        this._onActivateToolBtnClick(event);
      });
  }

  private _onActivateToolBtnClick(event?: MouseEvent): void {
    if (!this.menuTrigger) {
      return;
    }

    if (!event && this.settings.actionMode === 'contextmenu') {
      this.toolActivated.emit('LMB');
    }

    if (this.isEraserActive$.getValue()) {
      this._cornerstoneService.toolActivate(CornerstoneTool.EmptyTool);
      return;
    }

    this._activateTool();
    this._cornerstoneService.setBrushSize(this.eraserSettings.size);
  }

  private _activateTool(): void {
    this._cornerstoneService.toolActivate(
      `Erase${this.eraserSettings.shape}Brush` as CornerstoneTool
    );
  }

  public updateComponent(): void {
    this._cdr.markForCheck();
  }

  private _checkEraserActivated(currentTool: CornerstoneTool | null): boolean {
    if (!currentTool) {
      return false;
    }

    return [
      CornerstoneTool.EraseCircleBrush,
      CornerstoneTool.EraseSquareBrush,
    ].includes(currentTool);
  }

  private _initializeHotKeys(): void {
    this._keyInteractionService
      .initializeHotKey(
        HotKey.E,
        new HotKeySettings<EraserToolComponentKeyActionName>({
          keyActionName: 'EraserActivate',
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        if (this._stateManagerProjectService.isDisabledAllToolsForChangeMask) {
          return;
        }

        this.eraserAction.trigger();
      });

    this._keyInteractionService
      .initializeHotKey(
        HotKey.Escape,
        new HotKeySettings<EraserToolComponentKeyActionName>({
          keyActionName: 'ExitEraserMenu',
          priority: 3,
          isActive: !!this.settings.openMenu,
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.menuTrigger.closeMenu();
      });
  }
}
